/**
 * Math reading: MathML → spoken English ("x squared plus 1", "3 fourths",
 * "the square root of b squared minus 4 A c, end root").
 *
 * Used by the text extractor so math in the content is read as math instead of
 * the raw glyph soup textContent gives ("x2+1"). Also handles the renderings
 * MathJax (v2/v3) and KaTeX put on pages: their visual markup is dropped and the
 * MathML they include for assistive technology is read.
 *
 * Wording loosely follows MathSpeak/ClearSpeak conventions, kept short for students.
 */

/** Math renderers: container → the MathML inside it */
const RENDERER_CONTAINERS = 'mjx-container, .MathJax, .MathJax_CHTML, .MathJax_SVG, .MathJax_Display, .katex';

const OPERATORS: Record<string, string> = {
  '+': 'plus', '-': 'minus', '−': 'minus', '–': 'minus', '±': 'plus or minus', '∓': 'minus or plus',
  '×': 'times', '·': 'times', '⋅': 'times', '*': 'times', '∗': 'times', '÷': 'divided by', '/': 'divided by',
  '=': 'equals', '≠': 'is not equal to', '<': 'is less than', '>': 'is greater than',
  '≤': 'is less than or equal to', '≥': 'is greater than or equal to', '⩽': 'is less than or equal to', '⩾': 'is greater than or equal to',
  '≈': 'is approximately equal to', '≡': 'is equivalent to', '∝': 'is proportional to', '≅': 'is congruent to', '∼': 'is similar to',
  '→': 'goes to', '⟶': 'goes to', '⇒': 'implies', '⇔': 'if and only if', '↔': 'if and only if',
  '∈': 'is in', '∉': 'is not in', '⊂': 'is a subset of', '⊆': 'is a subset of', '∪': 'union', '∩': 'intersection',
  '∞': 'infinity', '∂': 'partial', '∇': 'del', '∠': 'angle', '°': 'degrees', '%': 'percent', '!': 'factorial',
  '′': 'prime', '″': 'double prime', '⊥': 'is perpendicular to', '∥': 'is parallel to', '∴': 'therefore', '∵': 'because',
  '∑': 'sum', '∏': 'product', '∫': 'integral', '∮': 'contour integral', '√': 'square root',
  '(': 'open paren', ')': 'close paren', '[': 'open bracket', ']': 'close bracket', '{': 'open brace', '}': 'close brace',
  '|': 'vertical bar', ',': ',', ';': ';', ':': ':', '.': 'point', '…': 'dot dot dot', '⋯': 'dot dot dot',
  '⁡': '', '⁢': '', '⁣': '', '⁤': 'plus',
};

const LETTERS: Record<string, string> = {
  'α': 'alpha', 'β': 'beta', 'γ': 'gamma', 'δ': 'delta', 'ε': 'epsilon', 'ϵ': 'epsilon', 'ζ': 'zeta', 'η': 'eta',
  'θ': 'theta', 'ϑ': 'theta', 'ι': 'iota', 'κ': 'kappa', 'λ': 'lambda', 'μ': 'mu', 'ν': 'nu', 'ξ': 'xi', 'π': 'pi',
  'ρ': 'rho', 'σ': 'sigma', 'τ': 'tau', 'υ': 'upsilon', 'φ': 'phi', 'ϕ': 'phi', 'χ': 'chi', 'ψ': 'psi', 'ω': 'omega',
  'Γ': 'capital gamma', 'Δ': 'delta', 'Θ': 'capital theta', 'Λ': 'capital lambda', 'Π': 'capital pi',
  'Σ': 'capital sigma', 'Φ': 'capital phi', 'Ψ': 'capital psi', 'Ω': 'omega',
  'ℝ': 'the real numbers', 'ℕ': 'the natural numbers', 'ℤ': 'the integers', 'ℚ': 'the rationals', 'ℂ': 'the complex numbers',
  // A lone "a" is read as the article ("uh")
  'a': 'A',
};

const ORDINAL_DENOMINATORS: Record<string, [string, string]> = {
  '2': ['half', 'halves'], '3': ['third', 'thirds'], '4': ['fourth', 'fourths'], '5': ['fifth', 'fifths'],
  '6': ['sixth', 'sixths'], '7': ['seventh', 'sevenths'], '8': ['eighth', 'eighths'], '9': ['ninth', 'ninths'], '10': ['tenth', 'tenths'],
};

const BIG_OPERATORS: Record<string, string> = { '∑': 'the sum', '∏': 'the product', '∫': 'the integral', '∮': 'the contour integral', '⋃': 'the union', '⋂': 'the intersection' };

const ACCENTS: Record<string, string> = { '¯': 'bar', '‾': 'bar', '―': 'bar', '_': 'bar', '^': 'hat', 'ˆ': 'hat', '~': 'tilde', '˜': 'tilde', '˙': 'dot', '¨': 'double dot', '→': 'vector', '⃗': 'vector' };

const name = (el: Element) => el.localName.toLowerCase();
const kids = (el: Element) => Array.from(el.children);

/** Single token (number, letter, symbol) — no "end fraction"/"end root" needed around it */
function isSimple(el: Element | undefined): boolean {
  if (!el) return true;
  const n = name(el);
  if (n === 'mi' || n === 'mn' || n === 'mtext') return true;
  if ((n === 'mrow' || n === 'mstyle') && el.children.length === 1) return isSimple(el.children[0]);
  return false;
}

function text(el: Element): string {
  return (el.textContent || '').trim();
}

function operator(symbol: string): string {
  const s = symbol.trim();
  if (s in OPERATORS) return OPERATORS[s];
  if (s in LETTERS) return LETTERS[s];
  return s;
}

function identifier(el: Element): string {
  const s = text(el);
  if (s in LETTERS) return LETTERS[s];
  // Multi-letter names (sin, log, max) stay as they are; Greek letters inside are named
  return s.split('').map(c => c > '\u007f' && LETTERS[c] ? ` ${LETTERS[c]} ` : c).join('').replace(/\s+/g, ' ').trim();
}

function power(exp: Element): string {
  const e = text(exp);
  if (e === '2') return 'squared';
  if (e === '3') return 'cubed';
  if (e === '′' || e === "'") return 'prime';
  if (e === '″') return 'double prime';
  if (e === '°' || e === '∘') return 'degrees';
  if (e === '−1' || e === '-1') return 'inverse';
  if (e === 'T' && isSimple(exp)) return 'transpose';
  return isSimple(exp) ? `to the power of ${speak(exp)}` : `to the power of ${speak(exp)}, end power,`;
}

/** First child is a big operator (∑, ∫) — read limits as "from … to …" */
function bigOperator(base: Element | undefined): string | null {
  if (!base) return null;
  return BIG_OPERATORS[text(base)] ?? null;
}

function speak(el: Element): string {
  const c = kids(el);
  switch (name(el)) {
    case 'math': {
      const label = el.getAttribute('aria-label') || el.getAttribute('alttext');
      // alttext is often raw TeX ("\frac{1}{2}") — only use it when it reads like words
      if (label && !/[\\{}^_]/.test(label)) return label;
      return c.map(speak).join(' ');
    }
    case 'semantics':
      return c[0] ? speak(c[0]) : '';
    case 'annotation':
    case 'annotation-xml':
    case 'mphantom':
    case 'none':
    case 'mprescripts':
      return '';
    case 'mi':
      return identifier(el);
    case 'mn':
    case 'mtext':
    case 'ms':
      return text(el);
    case 'mo': {
      const s = text(el);
      return BIG_OPERATORS[s] ?? operator(s);
    }
    case 'mspace':
      return ' ';
    case 'mfrac': {
      const [num, den] = c;
      if (!num || !den) return c.map(speak).join(' ');
      const n = text(num), d = text(den);
      if (name(num) === 'mn' && name(den) === 'mn' && /^\d+$/.test(n) && ORDINAL_DENOMINATORS[d]) {
        const [one, many] = ORDINAL_DENOMINATORS[d];
        return `${n === '1' ? 'one' : n} ${n === '1' ? one : many}`;
      }
      if (isSimple(num) && isSimple(den)) return `${speak(num)} over ${speak(den)}`;
      return `the fraction ${speak(num)}, over ${speak(den)}, end fraction,`;
    }
    case 'msup':
      return `${speak(c[0])} ${c[1] ? power(c[1]) : ''}`;
    case 'msub':
      return `${speak(c[0])} sub ${c[1] ? speak(c[1]) : ''}`;
    case 'msubsup':
    case 'munderover': {
      const big = bigOperator(c[0]);
      if (big) return `${big} from ${c[1] ? speak(c[1]) : ''} to ${c[2] ? speak(c[2]) : ''} of`;
      return name(el) === 'msubsup'
        ? `${speak(c[0])} sub ${c[1] ? speak(c[1]) : ''} ${c[2] ? power(c[2]) : ''}`
        : `${speak(c[0])} from ${c[1] ? speak(c[1]) : ''} to ${c[2] ? speak(c[2]) : ''}`;
    }
    case 'munder': {
      const base = text(c[0] || el);
      if (base === 'lim') return `the limit as ${c[1] ? speak(c[1]).replace('goes to', 'approaches') : ''} of`;
      const big = bigOperator(c[0]);
      if (big) return `${big} over ${c[1] ? speak(c[1]) : ''} of`;
      const accent = c[1] ? ACCENTS[text(c[1])] : undefined;
      return accent ? `${speak(c[0])} underbar` : `${speak(c[0])} ${c[1] ? speak(c[1]) : ''}`;
    }
    case 'mover': {
      const accent = c[1] ? ACCENTS[text(c[1])] : undefined;
      if (accent === 'vector') return `vector ${speak(c[0])}`;
      if (accent) return `${speak(c[0])} ${accent}`;
      return `${speak(c[0])} ${c[1] ? speak(c[1]) : ''}`;
    }
    case 'msqrt': {
      const inner = c.map(speak).join(' ');
      return c.length === 1 && isSimple(c[0]) ? `the square root of ${inner}` : `the square root of ${inner}, end root,`;
    }
    case 'mroot': {
      const idx = c[1] ? text(c[1]) : '';
      const which = idx === '3' ? 'cube' : idx === '2' ? 'square' : `${idx}th`;
      return `the ${which} root of ${c[0] ? speak(c[0]) : ''}${isSimple(c[0]) ? '' : ', end root,'}`;
    }
    case 'mfenced': {
      const open = el.getAttribute('open') ?? '(';
      const close = el.getAttribute('close') ?? ')';
      const sep = (el.getAttribute('separators') ?? ',').trim()[0] || ',';
      return `${operator(open)} ${c.map(speak).join(` ${operator(sep)} `)} ${operator(close)}`;
    }
    case 'mtable':
      return c.map(speak).join('; ');
    case 'mtr':
    case 'mlabeledtr':
      return c.map(speak).join(', ');
    case 'mmultiscripts':
      return c.map(speak).join(' ');
    default:
      // mrow, mstyle, mpadded, menclose, merror, mtd, maction and anything unknown
      return c.length ? c.map(speak).join(' ') : text(el);
  }
}

/** Spoken English for one <math> element */
export function mathToSpeech(math: Element): string {
  return speak(math)
    .replace(/\s+/g, ' ')
    .replace(/\s+([,;:])/g, '$1')
    .replace(/([,;:])(?:\s*[,;:])+/g, '$1')
    .replace(/,\s*$/, '')
    .trim();
}

/**
 * In a detached clone of page content: replace each math renderer (MathJax,
 * KaTeX) with the MathML it carries, then each <math> with its spoken form.
 */
export function speakMathIn(root: Element | DocumentFragment): void {
  root.querySelectorAll(RENDERER_CONTAINERS).forEach(container => {
    if (!root.contains(container)) return; // already replaced along with an outer container
    const math = container.querySelector('math');
    if (math) container.replaceWith(math);
  });
  // MathJax v2 keeps the TeX source in <script type="math/tex"> and a preview span
  root.querySelectorAll('script[type^="math/"], .MathJax_Preview').forEach(el => el.remove());

  root.querySelectorAll('math').forEach(math => {
    if (!root.contains(math)) return;
    const spoken = mathToSpeech(math);
    // No space before following punctuation ("… squared." not "… squared .")
    const next = math.nextSibling?.textContent ?? '';
    const after = /^[.,;:!?)]/.test(next) ? '' : ' ';
    math.replaceWith(document.createTextNode(spoken ? ` ${spoken}${after}` : ' '));
  });
}

/** Does this element contain math (MathML or a MathJax/KaTeX rendering)? */
export function containsMath(root: Element | DocumentFragment): boolean {
  return !!root.querySelector(`math, ${RENDERER_CONTAINERS}`);
}
